import React from "react";
import { useStateManager } from "bcms-react";
import { Account } from "model";

import CustomAccordion, { CustomAccordionContent, CustomAccordionToggle } from 'components/custom-ui/CustomAccordion';
import DateSelect from 'components/custom-ui/DateSelect';
import { PrimaryButton, SecondaryButton } from 'components/ui';
import { FormButtonRow, HorizontalFormRow } from 'components/shared/containers/FormContainer';
import { FormContainer, FormRowView } from 'components/shared';
import IconToggle from 'components/shared/views/IconToggle';
import CustomSelect, { CustomAccountSelect, CustomSelectItem } from 'components/custom-ui/CustomSelect';
import { SVGAsset } from 'res';

/**
 * @function StatementSearch
 * @returns {React.Component}
 * @description The statement search accordion
 * @exported true
 * @example
 * <StatementSearch />
 */
export default function StatementSearch(props) {
    const { data, ui, onAccountChange, onChange, onAccordionToggle, onAdvancedSearchToggle, onUpdate } = useStateManager();
    const state = data.search;

    // Initalize the account, and date objects if data has been set in state
    const selectedAccount = state.account !== "" && state.account !== "all" ? Account.initByType(state.account) : state.account;
    const startDate = state.startDate !== "" ? state.startDate : "";
    const endDate = state.endDate !== "" ? state.endDate : "";
    const hasDateRangeError = startDate.isAfter(endDate);

    const clear = evt => !hasDateRangeError && onUpdate(state.reset());
    const search = evt => onUpdate(state.filter());

    return (
        <CustomAccordion isOpen={ ui.searchAccordion.isActive }>
            <div className="accordion-toggle">
                <IconToggle icon="MagnifyingGlassIcon" text="Search" dataPoint="Search" isActive={ ui.advancedSearch.isActive } onClick={ onAdvancedSearchToggle }/>
            </div>
            
            <CustomAccordionContent className="search-toggle-content-container flex-container flex-vertical">
                <CustomAccordionToggle onClick={ onAccordionToggle }>
                    <SVGAsset className="close-icon" id="CloseX" />
                </CustomAccordionToggle>
                
                <FormContainer className="small-form-width">
                    <FormRowView className="first-custom-select-row">
                        <CustomAccountSelect className="statements-account-select" value={ selectedAccount } accounts={ Account.initAll() } onChange={ onAccountChange }
                        active={ ui.accountDD.isActive } data-point="Account_DD">
                            <CustomSelectItem value="all">All Accounts</CustomSelectItem>
                        </CustomAccountSelect>
                    </FormRowView>
                    
                    { ui.advancedSearch.isActive &&
                        <>
                            <HorizontalFormRow>
                                <FormRowView text="Start Date">
                                    <DateSelect data-point="Start_Date" enablePast={ true } enableFuture={ false } 
                                    value={ startDate } onChange={ onChange("startDate") } active={ ui.startDate.isActive } />
                                </FormRowView>
                                
                                <FormRowView text="End Date">
                                    <DateSelect data-point="End_Date" enablePast={ true } enableFuture={ false }
                                     value={ endDate } onChange={ onChange("endDate") } active={ ui.endDate.isActive } />
                                </FormRowView>
                            </HorizontalFormRow>

                            { hasDateRangeError &&<DateRangeErrorPrompt /> }
                            
                            <FormRowView text="Document Name">
                                <CustomSelect value={ state.document } data-point="Document_Name_DD" onChange={ onChange("document") } active={ ui.documentDD.isActive }>
                                    <CustomSelectItem value="all" data-point="All_Document_Names">All Document Names</CustomSelectItem>
                                </CustomSelect>
                            </FormRowView>
                            
                            <FormRowView text="Category">
                                <CustomSelect value={ state.category } data-point="Category_DD" onChange={ onChange("category") } active={ ui.categoryDD.isActive }>
                                    <CustomSelectItem value="all" data-point="All_Categories">All Categories</CustomSelectItem>
                                </CustomSelect>
                            </FormRowView>
                        </> }
                    
                        <FormButtonRow>
                            <SecondaryButton text="Clear" onClick={ clear } />
                            <PrimaryButton className={hasDateRangeError ?"disabled-button" : ""} text="Search" onClick={ search } />
                        </FormButtonRow>
                </FormContainer>
            </CustomAccordionContent>
        </CustomAccordion>
    );
}

/**
 * @function DateRangeErrorPrompt 
 * @returns {React.Component}
 * @description Error prompt for selecting an end date in the date range before the start date.
 */
function DateRangeErrorPrompt(props){
    return(
        <div className="error-prompt-container flex-container flex-horizontal align-center">
            <SVGAsset id="AlertTriangleIcon" className="set-width error-prompt-icon" />
            <span className="flex-width error-text">Please select a valid date range within 180 days</span>
        </div>
    );
}